import { type FC, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useTimer } from 'react-timer-hook'
import cn from 'classnames'
import { useAuth } from '@egw/auth'
import { useTranslation } from '@egw/i18n'
import { ModalDrawer, Button, ButtonVariant } from '@egw/components'
import { useNavigate } from 'react-router'
import { WEALTH_ROUTES } from '@egw/constants'
import { configClient } from '@egw/config'

const TIMER_DURATION = 59 * 1000

const getIdleTimeout = (config: { env: string }): number => {
  return config.env === 'prod' ? 4 * 60 * 1000 : 90 * 60 * 1000
}

interface IdleModalProps {
  message: string
  hasSignOut: boolean
  signOutRoute: string
  onClose: () => void
}

const IdleContent: FC<IdleModalProps> = ({ message, hasSignOut, signOutRoute, onClose }) => {
  const { t } = useTranslation('features-auth')
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const { seconds } = useTimer({
    expiryTimestamp: new Date(Date.now() + TIMER_DURATION),
    autoStart: true,
    onExpire: async () => {
      onClose()
      await signOut()
      void navigate(`${signOutRoute}?session-timeout=true`)
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <span className="text-base">{message}</span>
      <div
        className={cn('justify-center items-center flex text-[3.75rem] py-12 bg-bglight', {
          'text-error': seconds <= 10,
        })}
      >
        00 : {seconds >= 0 ? <>{seconds < 10 ? `0${seconds.toString()}` : seconds}</> : '00'}
      </div>
      <Button
        id="sessionModalCta"
        onClick={() => {
          onClose()
        }}
      >
        {t('idle.extend_time')}
      </Button>
      {hasSignOut ? (
        <Button
          id="signOutCta"
          variant={ButtonVariant.Secondary}
          onClick={() => {
            void signOut()
            void navigate(WEALTH_ROUTES.SIGN_IN.path)
          }}
        >
          {t('idle.sign_out')}
        </Button>
      ) : null}
    </div>
  )
}

export interface IdleTimerProps {
  title: string
  message: string
  hasSignOut?: boolean
  signOutRoute: string
}

export const IdleTimer: FC<IdleTimerProps> = ({ title, message, signOutRoute, hasSignOut = true }) => {
  const [isOpen, setIsOpen] = useState(false)

  const config = configClient.getConfig() as { env: 'local' | 'dev' | 'prod' }

  useIdleTimer({
    onIdle: () => {
      setIsOpen(true)
    },
    timeout: getIdleTimeout(config),
    throttle: 1000,
  })

  return (
    <ModalDrawer
      isOpen={isOpen}
      title={title}
      onClose={() => {
        setIsOpen(false)
      }}
    >
      {isOpen ? (
        <IdleContent
          message={message}
          hasSignOut={hasSignOut}
          signOutRoute={signOutRoute}
          onClose={() => {
            setIsOpen(false)
          }}
        />
      ) : null}
    </ModalDrawer>
  )
}
